/**
 * Toast — Ephemeral notification shown at the bottom of the screen. Uses a
 * function-based API (not a React component you render in JSX).
 *
 * Toast Component Guidelines
 * - Use toasts for transient feedback the user doesn't need to act on (e.g. "Saved",
 *   "Copied to clipboard"). They auto-dismiss and shouldn't contain critical info.
 * - For persistent errors or validation messages, use inline `<FieldError>` or an
 *   alert banner instead — toasts disappear before the user may notice them.
 * - Choose the variant to match severity: `success` for confirmations, `error` for
 *   failures, `warning` for recoverable issues, `info` for neutral updates.
 * - Keep `title` under ~60 characters. Use `description` only when the title alone
 *   isn't enough context (e.g. "File exceeds 10MB limit").
 * - Use `action` sparingly — only when the user can take an immediate follow-up
 *   action like undo, retry, or navigate. Most toasts shouldn't have one.
 * @see https://sonner.emilkowal.ski Sonner documentation
 * @see https://ui.shadcn.com/docs/components/sonner shadcn/ui Sonner documentation
 */
import type { AlertVariant } from "@replo/design-system/blocks/alert/Alert";

import React from "react";

import { AlertIcon } from "@replo/design-system/blocks/alert/Alert";
import { Button } from "@replo/design-system/components/button/Button";
import twMerge from "@replo/design-system/utils/twMerge";
import { X } from "lucide-react";
import { toast as sonnerToast } from "sonner";

type ToastVariant = Extract<
  AlertVariant,
  "success" | "error" | "warning" | "info"
>;

interface ToastAction {
  label: string;
  onClick: () => void;
}

interface ToastProps {
  id: string | number;
  variant: ToastVariant;
  title: string;
  description?: string;
  action?: ToastAction;
}

function Toast({ id, variant, title, description, action }: ToastProps) {
  const isTitleOnly = !description && !action;
  return (
    <div
      className={twMerge(
        "flex w-full min-w-[268px] max-w-[400px] gap-3 rounded-xl border border-0.5 border-border bg-white p-4 shadow-2xl",
        isTitleOnly ? "items-center" : "items-start",
      )}
    >
      <AlertIcon variant={variant} />

      <div className="flex flex-col gap-3">
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <span className="header-h5">{title}</span>
          {description && (
            <span className="text-sm text-muted">{description}</span>
          )}
        </div>

        {action && (
          <Button
            variant="primary"
            size="sm"
            onClick={() => {
              action.onClick();
              sonnerToast.dismiss(id);
            }}
          >
            {action.label}
          </Button>
        )}
      </div>

      <div className="flex shrink-0 items-center gap-1">
        <Button
          icon
          size="sm"
          variant="tertiary"
          onClick={() => sonnerToast.dismiss(id)}
        >
          <X />
        </Button>
      </div>
    </div>
  );
}

function showToast(
  variant: ToastVariant,
  options: {
    title: string;
    description?: string;
    action?: ToastAction;
  },
) {
  const { title, description, action } = options;

  return sonnerToast.custom((id) => (
    <Toast
      id={id}
      variant={variant}
      title={title}
      description={description}
      action={action}
    />
  ));
}

const toast = Object.assign(
  (options: { title: string; description?: string; action?: ToastAction }) => {
    return showToast("success", options);
  },
  {
    success: (options: {
      title: string;
      description?: string;
      action?: ToastAction;
    }) => showToast("success", options),
    error: (options: {
      title: string;
      description?: string;
      action?: ToastAction;
    }) => showToast("error", options),
    warning: (options: {
      title: string;
      description?: string;
      action?: ToastAction;
    }) => showToast("warning", options),
    info: (options: {
      title: string;
      description?: string;
      action?: ToastAction;
    }) => showToast("info", options),
  },
);

export { toast };
